import { authFetch } from '../../api/csrf';
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { internalTicketService } from '../../api/crm/support/internalSupportService';
import { internalTicketTypeService } from '../../api/crm/config/configService';
import { employeeApi } from '../../api/employeeApi';
import apiClient from '../../api/apiClient';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import {
    Plus,
    Search,
    Filter,
    MessageSquare,
    Paperclip,
    Send,
    Star,
    CheckCircle,
    Clock,
    AlertCircle,
    X,
    FileText,
    Download,
    User,
    Building2,
    Check,
    ChevronRight,
    ArrowLeft,
    CornerUpLeft
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/common/Button/Button';

const InternalTickets = () => {
    const { user: currentUser } = useAuth();
    const queryClient = useQueryClient();
    const isSuperAdmin = currentUser?.role === 'super_admin';

    // Active main tab: 'raised' or 'department'
    const [activeTab, setActiveTab] = useState('raised');

    // Pagination State
    const [page, setPage] = useState(1);
    const limit = 4;

    // Detail Pane Selection
    const [selectedTicketId, setSelectedTicketId] = useState(null);

    // Search and filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Today's date string (YYYY-MM-DD) — used to restrict future dates
    const todayStr = new Date().toISOString().split('T')[0];

    // Reset page to 1 when filters or tabs change
    React.useEffect(() => {
        setPage(1);
    }, [searchQuery, statusFilter, priorityFilter, departmentFilter, activeTab, startDate, endDate]);

    // Validate date range — only when BOTH dates are set
    React.useEffect(() => {
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            toast.error("From Date cannot be after To Date");
            setEndDate('');
        }
    }, [startDate, endDate]);

    // Create Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTicketData, setNewTicketData] = useState({
        title: '',
        type: '',
        department: '',
        employeeId: '',
        description: '',
        priority: 'Medium',
        attachments: []
    });

    // Rating / Feedback State
    const [rating, setRating] = useState(0);
    const [feedbackComment, setFeedbackComment] = useState('');

    // New Comment State
    const [commentText, setCommentText] = useState('');
    const [commentFiles, setCommentFiles] = useState([]);
    const [replyingTo, setReplyingTo] = useState(null); // { commentId, authorName, content }

    // Form validation errors for create modal
    const [formErrors, setFormErrors] = useState({});
    const [formTouched, setFormTouched] = useState({});

    // Ref for scrolling comments container
    const commentsContainerRef = React.useRef(null);



    // Queries
    // 0. Fetch Ticket KPIs (scoped by department for super admin)
    const { data: ticketKPIsResponse } = useQuery({
        queryKey: ['internalTicketKPIs', isSuperAdmin ? departmentFilter : 'own'],
        queryFn: () => internalTicketService.getKPIs(
            isSuperAdmin ? { department: departmentFilter } : {}
        )
    });
    const ticketKPIs = ticketKPIsResponse?.data;

    const activeKPIs = isSuperAdmin 
        ? (ticketKPIs?.department || ticketKPIs?.raised)
        : (activeTab === 'raised' ? ticketKPIs?.raised : ticketKPIs?.department);


    // 1. Fetch Tickets
    const { data: ticketsResponse, isLoading: ticketsLoading } = useQuery({
        queryKey: ['internalTickets', isSuperAdmin ? 'all' : activeTab, searchQuery, statusFilter, priorityFilter, departmentFilter, startDate, endDate, page],
        queryFn: () => internalTicketService.getAll({
            scope: isSuperAdmin ? 'department' : activeTab,
            search: searchQuery,
            status: statusFilter,
            priority: priorityFilter,
            department: departmentFilter,
            startDate,
            endDate,
            page,
            limit
        })
    });
    const tickets = ticketsResponse?.data || [];
    const totalTickets = ticketsResponse?.total || tickets.length;
    const totalPages = Math.ceil(totalTickets / limit);

    // 2. Fetch selected ticket details
    const { data: ticketDetailsResponse, isLoading: detailsLoading } = useQuery({
        queryKey: ['internalTicketDetails', selectedTicketId],
        queryFn: () => internalTicketService.getById(selectedTicketId),
        enabled: !!selectedTicketId,
        refetchInterval: 5000
    });
    const selectedTicket = ticketDetailsResponse?.data;

    React.useEffect(() => {
        if (selectedTicketId) {
            const timer = setTimeout(() => {
                if (commentsContainerRef.current) {
                    commentsContainerRef.current.scrollTop = commentsContainerRef.current.scrollHeight;
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [selectedTicketId]);

    React.useEffect(() => {
        if (selectedTicket?.comments) {
            const timer = setTimeout(() => {
                if (commentsContainerRef.current) {
                    commentsContainerRef.current.scrollTo({
                        top: commentsContainerRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [selectedTicket?.comments?.length]);

    // 3. Fetch Master Lists
    const { data: ticketTypesResponse } = useQuery({
        queryKey: ['internalTicketTypes'],
        queryFn: () => internalTicketTypeService.getDropdown()
    });
    const ticketTypes = ticketTypesResponse?.data || [];

    const { data: distinctDepartmentsResponse } = useQuery({
        queryKey: ['departmentsDistinct'],
        queryFn: () => apiClient.get('/hr/departments/distinct-employee')
    });
    const filterDepartmentsList = (() => {
        const r = distinctDepartmentsResponse;
        if (!r) return [];
        if (Array.isArray(r)) return r.map(d => typeof d === 'string' ? { name: d } : d);
        if (Array.isArray(r.departments)) return r.departments.map(d => typeof d === 'string' ? { name: d } : d);
        if (r.data && Array.isArray(r.data.departments)) return r.data.departments.map(d => typeof d === 'string' ? { name: d } : d);
        if (Array.isArray(r.data)) return r.data.filter(d => d?.isActive !== false).map(d => typeof d === 'string' ? { name: d } : d);
        return [];
    })();

    // State for department pagination/search inside Modal
    const [deptSearch, setDeptSearch] = useState('');
    const [deptPage, setDeptPage] = useState(1);
    const [departmentsList, setDepartmentsList] = useState([]);
    const [hasMoreDept, setHasMoreDept] = useState(false);
    const [loadingDept, setLoadingDept] = useState(false);
    const [loadingMoreDept, setLoadingMoreDept] = useState(false);

        // State for employee pagination/search inside Modal
    const [empSearch, setEmpSearch] = useState('');
    const [empPage, setEmpPage] = useState(1);
    const [employeesList, setEmployeesList] = useState([]);
    const [hasMoreEmp, setHasMoreEmp] = useState(false);
    const [loadingEmp, setLoadingEmp] = useState(false);
    const [loadingMoreEmp, setLoadingMoreEmp] = useState(false);
    const [targetDeptEmployeeCount, setTargetDeptEmployeeCount] = useState(null);

    // Fetch Departments (Modal)
    const fetchDepartments = async (pageToFetch, searchString, isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMoreDept(true);
            } else {
                setLoadingDept(true);
            }
            const params = {
                paginated: 'true',
                isActive: 'true',
                limit: 20,
                page: pageToFetch,
                search: searchString
            };
            const response = await apiClient.get('/hr/departments', { params });
            const data = response?.data || response;
            const depts = data?.departments || [];
            const pagination = data?.pagination || {};

            setDepartmentsList(prev => isLoadMore ? [...prev, ...depts] : depts);
            setHasMoreDept(!!pagination.hasNextPage);
        } catch (error) {
        } finally {
            setLoadingDept(false);
            setLoadingMoreDept(false);
        }
    };

    // Fetch Employees (Modal)
    const fetchEmployees = async (pageToFetch, searchString, departmentName, isLoadMore = false) => {
        if (!departmentName) {
            setEmployeesList([]);
            setHasMoreEmp(false);
            return;
        }
        try {
            if (isLoadMore) {
                setLoadingMoreEmp(true);
            } else {
                setLoadingEmp(true);
            }
            const params = {
                page: pageToFetch,
                limit: 20,
                search: searchString,
                department: departmentName
            };
            const response = await employeeApi.getEmployeePicker(params);
            const data = response?.data || response;
            const emps = data?.employees || [];
            const pagination = data?.pagination || {};

            setEmployeesList(prev => isLoadMore ? [...prev, ...emps] : emps);
            setHasMoreEmp(!!pagination.hasNextPage);
            if (!searchString) {
                setTargetDeptEmployeeCount(pagination.total ?? emps.length);
            }
        } catch (error) {
        } finally {
            setLoadingEmp(false);
            setLoadingMoreEmp(false);
        }
    };

    // Fetch initial departments when modal opens
    useEffect(() => {
        if (showCreateModal) {
            setDeptSearch('');
            setDeptPage(1);
            fetchDepartments(1, '');
        }
    }, [showCreateModal]);

    // Fetch initial employees when selected department changes
    useEffect(() => {
        setTargetDeptEmployeeCount(null);
        if (showCreateModal && newTicketData.department) {
            setEmpSearch('');
            setEmpPage(1);
            fetchEmployees(1, '', newTicketData.department);
        } else {
            setEmployeesList([]);
            setHasMoreEmp(false);
        }
    }, [showCreateModal, newTicketData.department]);

    // Re-validate department field when its employee count changes
    useEffect(() => {
        if (formTouched.department) {
            setFormErrors(prev => {
                const errors = validateCreateForm(newTicketData);
                return { ...prev, department: errors.department };
            });
        }
    }, [targetDeptEmployeeCount]);

    // Handlers for department searches & infinite scroll
    const handleDeptSearch = (term) => {
        setDeptSearch(term);
        setDeptPage(1);
        fetchDepartments(1, term);
    };

    const handleDeptScrollToBottom = () => {
        if (hasMoreDept && !loadingDept && !loadingMoreDept) {
            const nextPage = deptPage + 1;
            setDeptPage(nextPage);
            fetchDepartments(nextPage, deptSearch, true);
        }
    };

    // Handlers for employee searches & infinite scroll
    const handleEmpSearch = (term) => {
        setEmpSearch(term);
        setEmpPage(1);
        fetchEmployees(1, term, newTicketData.department);
    };

    const handleEmpScrollToBottom = () => {
        if (hasMoreEmp && !loadingEmp && !loadingMoreEmp) {
            const nextPage = empPage + 1;
            setEmpPage(nextPage);
            fetchEmployees(nextPage, empSearch, newTicketData.department, true);
        }
    };

    const handleDownloadAttachment = async (e, fileUrl, fileName) => {
        e.preventDefault();
        try {
            const response = await authFetch(fileUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            window.open(fileUrl, '_blank');
        }
    };

    // Mutations
    // 1. Create Ticket
    const createTicketMutation = useMutation({
        mutationFn: (data) => internalTicketService.create(data),
        onSuccess: () => {
            toast.success('Ticket raised successfully!');
            setShowCreateModal(false);
            setNewTicketData({
                title: '',
                type: ticketTypes[0]?._id || '',
                department: '',
                employeeId: '',
                description: '',
                priority: 'Medium',
                attachments: []
            });
            queryClient.invalidateQueries({ queryKey: ['internalTickets'] });
            queryClient.invalidateQueries({ queryKey: ['internalTicketKPIs'] });
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to raise ticket');
        }
    });

    // 2. Add Comment
    const addCommentMutation = useMutation({
        mutationFn: ({ id, data }) => internalTicketService.addComment(id, data),
        onSuccess: () => {
            toast.success('Comment added successfully');
            setCommentText('');
            setCommentFiles([]);
            queryClient.invalidateQueries({ queryKey: ['internalTicketDetails', selectedTicketId] });
            queryClient.invalidateQueries({ queryKey: ['internalTickets'] });
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to post comment');
        }
    });

    // 3. Update Status (Start Processing / Mark Resolved / Close)
    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status }) => internalTicketService.close(id, { status }),
        onSuccess: (data, variables) => {
            toast.success(`Ticket marked as ${variables.status}`);
            queryClient.invalidateQueries({ queryKey: ['internalTicketDetails', selectedTicketId] });
            queryClient.invalidateQueries({ queryKey: ['internalTickets'] });
            queryClient.invalidateQueries({ queryKey: ['internalTicketKPIs'] });
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update ticket status');
        }
    });

    // 4. Rate and Close Ticket
    const rateTicketMutation = useMutation({
        mutationFn: ({ id, rating, comment }) => internalTicketService.rate(id, { rating, comment }),
        onSuccess: () => {
            toast.success('Thank you for your feedback! Ticket closed.');
            setRating(0);
            setFeedbackComment('');
            queryClient.invalidateQueries({ queryKey: ['internalTicketDetails', selectedTicketId] });
            queryClient.invalidateQueries({ queryKey: ['internalTickets'] });
            queryClient.invalidateQueries({ queryKey: ['internalTicketKPIs'] });
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to submit feedback');
        }
    });

    // Populate defaults when showing modal
    const handleOpenCreateModal = () => {
        setNewTicketData({
            title: '',
            type: ticketTypes[0]?._id || '',
            department: '',
            employeeId: '',
            description: '',
            priority: 'Medium',
            attachments: []
        });
        setFormErrors({});
        setFormTouched({});
        setShowCreateModal(true);
    };

    // Handle ticket creation submit
    const handleCreateTicket = (e) => {
        e.preventDefault();

        // Run full validation
        const errors = validateCreateForm(newTicketData);
        // Mark all fields touched to show all errors
        setFormTouched({ title: true, type: true, department: true, employeeId: true, description: true });
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        createTicketMutation.mutate({
            title: newTicketData.title.trim(),
            ticketType: newTicketData.type,
            description: newTicketData.description.trim(),
            department: newTicketData.department,
            concernedEmployee: newTicketData.employeeId || null,
            priority: newTicketData.priority,
            // attachments are real Cloudinary objects: { fileName, fileUrl, fileSize }
            attachments: newTicketData.attachments
        });
    };

    // Field validation helper
    const validateCreateForm = (data) => {
        const errors = {};
        if (!data.title.trim()) errors.title = 'Title is required.';
        else if (data.title.trim().length < 5) errors.title = 'Title must be at least 5 characters.';
        else if (data.title.trim().length > 120) errors.title = 'Title cannot exceed 120 characters.';
        if (!data.type) errors.type = 'Please select a ticket type.';
        if (!data.department) {
            errors.department = 'Please select a target department.';
        } else if (targetDeptEmployeeCount === 0) {
            errors.department = 'Selected department has no active employees.';
        }
        if (!data.employeeId) {
            errors.employeeId = 'Please select a target employee.';
        }
        if (!data.description.trim()) errors.description = 'Description is required.';
        else if (data.description.trim().length < 10) errors.description = 'Description must be at least 10 characters.';
        return errors;
    };

    // Per-field blur handler for real-time validation
    const handleFieldBlur = (field) => {
        setFormTouched(prev => ({ ...prev, [field]: true }));
        const errors = validateCreateForm(newTicketData);
        setFormErrors(errors);
    };

    // Per-field change: clear its error if now valid
    const handleFieldChange = (field, value) => {
        const updated = { ...newTicketData, [field]: value };
        setNewTicketData(updated);
        if (formTouched[field]) {
            const errors = validateCreateForm(updated);
            setFormErrors(errors);
        }
    };

    // Upload state for create modal and comment
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadingCommentFiles, setUploadingCommentFiles] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const isAllowedFileType = (file) => {
        if (!file) return false;
        const mime = file.type || '';
        const name = (file.name || '').toLowerCase();
        if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) {
            return true;
        }
        const allowedExtensions = [
            '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx', '.txt', '.rtf'
        ];
        return allowedExtensions.some(ext => name.endsWith(ext));
    };

    const uploadFiles = async (files) => {
        if (!files.length) return;
        
        const invalidFiles = files.filter(file => !isAllowedFileType(file));
        if (invalidFiles.length > 0) {
            toast.error('Only documents, images, videos, and audio recordings are allowed.');
            return;
        }

        setUploadingFiles(true);
        try {
            const uploaded = await Promise.all(
                files.map(file => internalTicketService.uploadAttachment(file, 'ticket-attachments'))
            );
            setNewTicketData(prev => ({
                ...prev,
                attachments: [
                    ...prev.attachments,
                    ...uploaded.map(u => ({ fileName: u.fileName, fileUrl: u.fileUrl, fileSize: u.fileSize }))
                ]
            }));
            toast.success(`${files.length} file(s) uploaded to cloud`);
        } catch (err) {
            toast.error('Upload failed: ' + (err.message || 'Unknown error'));
        } finally {
            setUploadingFiles(false);
        }
    };

    // Real Cloudinary upload for ticket create modal
    const handleFileAttach = (e) => {
        const files = Array.from(e.target.files);
        uploadFiles(files);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!uploadingFiles) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (!uploadingFiles) {
            const files = Array.from(e.dataTransfer.files);
            uploadFiles(files);
        }
    };

    // Real Cloudinary upload for comment attachments
    const handleCommentFileAttach = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const invalidFiles = files.filter(file => !isAllowedFileType(file));
        if (invalidFiles.length > 0) {
            toast.error('Only documents, images, videos, and audio recordings are allowed.');
            e.target.value = '';
            return;
        }

        setUploadingCommentFiles(true);
        try {
            const uploaded = await Promise.all(
                files.map(file => internalTicketService.uploadAttachment(file, 'ticket-comments'))
            );
            setCommentFiles(prev => [
                ...prev,
                ...uploaded.map(u => ({ fileName: u.fileName, fileUrl: u.fileUrl, fileSize: u.fileSize }))
            ]);
            toast.success(`${files.length} file(s) uploaded`);
        } catch (err) {
            toast.error('Upload failed: ' + (err.message || 'Unknown error'));
        } finally {
            setUploadingCommentFiles(false);
            e.target.value = '';
        }
    };

    // Helper: detect image file by extension or URL
    const isImageFile = (fileName = '', fileUrl = '') => {
        const name = (fileName || fileUrl || '').toLowerCase();
        return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.avif'].some(ext => name.includes(ext));
    };

    // Status Styling
    const getStatusStyle = (status) => {
        const ns = String(status || '').toLowerCase().replace(/[\s\-_]+/g, '');
        switch (ns) {
            case 'open':        return 'bg-blue-50 text-blue-700 border border-blue-200';
            case 'inprogress':  return 'bg-amber-50 text-amber-700 border border-amber-200';
            case 'resolved':    return 'bg-green-50 text-green-700 border border-green-200';
            case 'closed':
            case 'rated':       return 'bg-gray-100 text-gray-700 border border-gray-200';
            default:            return 'bg-gray-50 text-gray-600 border border-gray-200';
        }
    };

    // Priority Styling
    const getPriorityStyle = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high': return 'bg-red-50 text-red-700 border border-red-100';
            case 'medium': return 'bg-blue-50 text-blue-700 border border-blue-100';
            case 'low': return 'bg-gray-50 text-gray-700 border border-gray-100';
            default: return 'bg-gray-50 text-gray-700';
        }
    };

    // Handle Comment Submit
    const handleAddComment = (e) => {
        e.preventDefault();
        if (!commentText.trim() && commentFiles.length === 0) return;
        if (!commentText.trim()) { toast.error('Please type a message'); return; }

        addCommentMutation.mutate({
            id: selectedTicketId,
            data: {
                content: commentText.trim(),
                attachments: commentFiles,
                ...(replyingTo ? {
                    replyTo: {
                        commentId: replyingTo.commentId,
                        authorName: replyingTo.authorName,
                        content: replyingTo.content.substring(0, 200)
                    }
                } : {})
            }
        });
        setReplyingTo(null);
    };

    // Transition state/resolve ticket
    const updateTicketStatus = (newStatus) => {
        updateStatusMutation.mutate({
            id: selectedTicketId,
            status: newStatus
        });
    };

    // Feedback Submit
    const handleFeedbackSubmit = (e) => {
        e.preventDefault();
        if (rating === 0) {
            toast.error('Please select a star rating');
            return;
        }

        rateTicketMutation.mutate({
            id: selectedTicketId,
            rating,
            comment: feedbackComment.trim()
        });
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50/50 p-4 md:p-6 lg:p-8 space-y-6">
            
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
                        Internal Ticket Management
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Raise, track, and resolve IT, HR, and Facility support issues.
                    </p>
                </div>
                {!isSuperAdmin && (
                    <Button
                        variant="primary"
                        onClick={handleOpenCreateModal}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white px-5 py-2.5 rounded-lg flex items-center space-x-2 shadow-sm shadow-green-100"
                        icon={<Plus className="h-4.5 w-4.5" />}
                    >
                        Create Ticket
                    </Button>
                )}
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    {isSuperAdmin ? (
                        // Super admin sees ALL tickets in one unified view
                        <button className="pb-4 px-1 border-b-2 border-agrosaf-green text-agrosaf-green font-semibold text-sm cursor-default">
                            All Tickets
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => { setActiveTab('raised'); setSelectedTicketId(null); }}
                                className={`pb-4 px-1 border-b-2 font-semibold text-sm transition-colors ${
                                    activeTab === 'raised'
                                        ? 'border-agrosaf-green text-agrosaf-green'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                My Raised Tickets
                            </button>
                            <button
                                onClick={() => { setActiveTab('department'); setSelectedTicketId(null); }}
                                className={`pb-4 px-1 border-b-2 font-semibold text-sm transition-colors ${
                                    activeTab === 'department'
                                        ? 'border-agrosaf-green text-agrosaf-green'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Assigned Tickets
                            </button>
                        </>
                    )}
                </nav>
            </div>

            {/* KPI Cards — clickable to filter by status */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 mt-4 font-sans">
                <button
                    type="button"
                    onClick={() => setStatusFilter('all')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'all' ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Tickets</p>
                        <p className="text-2xl font-bold text-gray-800 mt-1">{activeKPIs?.total || 0}</p>
                        {statusFilter === 'all' && <p className="text-[10px] text-blue-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                        <MessageSquare className="h-6 w-6 text-blue-500" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('Open')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'Open' ? 'border-red-400 ring-2 ring-red-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Open</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">{activeKPIs?.open || 0}</p>
                        {statusFilter === 'Open' && <p className="text-[10px] text-red-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg">
                        <AlertCircle className="h-6 w-6 text-red-500" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('In Progress')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'In Progress' ? 'border-amber-400 ring-2 ring-amber-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">In Progress</p>
                        <p className="text-2xl font-bold text-amber-500 mt-1">{activeKPIs?.inProgress || 0}</p>
                        {statusFilter === 'In Progress' && <p className="text-[10px] text-amber-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg">
                        <Clock className="h-6 w-6 text-amber-500" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('Resolved')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'Resolved' ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Resolved</p>
                        <p className="text-2xl font-bold text-indigo-500 mt-1">{activeKPIs?.resolved || 0}</p>
                        {statusFilter === 'Resolved' && <p className="text-[10px] text-indigo-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-indigo-50 p-3 rounded-lg">
                        <Star className="h-6 w-6 text-indigo-500" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('Closed')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'Closed' ? 'border-green-400 ring-2 ring-green-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Closed</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">{activeKPIs?.closed || 0}</p>
                        {statusFilter === 'Closed' && <p className="text-[10px] text-green-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-green-500" />
                    </div>
                </button>
            </div>

            {/* Filters Row */}
            <div className="bg-white p-4 rounded-xl border border-gray-200/80 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="relative flex-1 w-full md:max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by ID, title, description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    />
                </div>
                <div className="flex flex-wrap w-full md:w-auto items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium">From:</span>
                        <input
                            type="date"
                            value={startDate}
                            max={endDate || todayStr}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-gray-700 bg-white"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium">To:</span>
                        <input
                            type="date"
                            value={endDate}
                            min={startDate || undefined}
                            max={todayStr}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-gray-700 bg-white"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white text-gray-700"
                    >
                        <option value="all">All Statuses</option>
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                        <option value="Rated">Rated</option>
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white text-gray-700"
                    >
                        <option value="all">All Priorities</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                    </select>
                    {/* Department filter — super admin only */}
                    {isSuperAdmin && (
                        <select
                            value={departmentFilter}
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                            className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white text-gray-700"
                        >
                            <option value="all">All Departments</option>
                            {filterDepartmentsList.map(dept => (
                                <option key={dept.id || dept._id || dept.name} value={dept.name}>{dept.name}</option>
                            ))}
                        </select>
                    )}
                    {(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || departmentFilter !== 'all' || startDate || endDate) && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery('');
                                setStatusFilter('all');
                                setPriorityFilter('all');
                                setDepartmentFilter('all');
                                setStartDate('');
                                setEndDate('');
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-semibold transition-colors"
                        >
                            <X className="h-3.5 w-3.5" />
                            Clear Filters
                        </button>
                    )}
                </div>
            </div>

            {/* Main Work Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Tickets List - Left/Main Columns */}
                <div className={`lg:col-span-1 space-y-4 ${selectedTicketId ? 'hidden lg:block' : 'block'}`}>

                    {/* Tickets List */}
                    <div className="space-y-2.5">
                        {ticketsLoading ? (
                            <div className="text-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
                                <Clock className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                                <p className="text-sm text-gray-500">Loading tickets...</p>
                            </div>
                        ) : tickets.length > 0 ? (
                            tickets.map(ticket => (
                                <div
                                    key={ticket._id}
                                    onClick={() => setSelectedTicketId(ticket._id)}
                                    className={`bg-white p-3.5 rounded-xl border transition-all cursor-pointer hover:shadow-md ${
                                        selectedTicketId === ticket._id
                                            ? 'border-agrosaf-green ring-1 ring-agrosaf-green/30 shadow-sm'
                                            : 'border-gray-200 hover:border-gray-300 shadow-sm'
                                    }`}
                                >
                                    <div className="flex justify-between items-start mb-1.5">
                                        <div className="flex items-center space-x-1.5">
                                            <span className="text-[10px] font-mono font-bold bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                                                {ticket.ticketId}
                                            </span>
                                            <span className="text-[10px] text-gray-400">•</span>
                                            <span className="text-[10px] text-gray-500 font-medium">
                                                {ticket.ticketType?.name || 'General'}
                                            </span>
                                            {ticket.department && (
                                                <>
                                                    <span className="text-[10px] text-gray-400">•</span>
                                                    <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-600 font-medium bg-indigo-50 px-1.5 py-0.5 rounded">
                                                        <Building2 className="h-2.5 w-2.5" />
                                                        {ticket.department}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {(() => {
                                            const ns = String(ticket.status || '').toLowerCase().replace(/[\s\-_]+/g, '');
                                            if (ns === 'rated' && ticket.feedback?.rating) {
                                                return (
                                                    <div className="flex flex-col items-end gap-1">
                                                        <div className="flex items-center gap-0.5" title={`Rated ${ticket.feedback.rating}/5`}>
                                                            {[1, 2, 3, 4, 5].map(s => (
                                                                <Star
                                                                    key={s}
                                                                    className={`h-3 w-3 ${s <= ticket.feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-gray-100'}`}
                                                                />
                                                            ))}
                                                        </div>
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-700 border border-gray-250">
                                                            Closed
                                                        </span>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${getStatusStyle(ticket.status)}`}>
                                                    {ticket.status}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <h3 className="text-sm font-bold text-gray-950 mb-0.5 line-clamp-1">
                                        {ticket.title}
                                    </h3>
                                    <p className="text-xs text-gray-550 line-clamp-1 mb-2">
                                        {ticket.description}
                                    </p>
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-100/70 text-[10px] text-gray-400">
                                        <div className="flex items-center space-x-2.5">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getPriorityStyle(ticket.priority)}`}>
                                                {ticket.priority}
                                            </span>
                                            {ticket.attachments?.length > 0 && (
                                                <span className="flex items-center text-gray-400">
                                                    <Paperclip className="h-2.5 w-2.5 mr-0.5" />
                                                    {ticket.attachments.length} files
                                                </span>
                                            )}
                                        </div>
                                        <span>
                                            Created {new Date(ticket.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                                <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-bold text-gray-900 mb-1">No Tickets Found</h3>
                                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                                    No ticket records match your filter criteria. Try expanding your filters or raise a new ticket.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-100 bg-white px-4 py-3 rounded-xl shadow-sm mt-4">
                            <div className="flex flex-1 justify-between sm:hidden">
                                <button
                                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={page === totalPages}
                                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-xs text-gray-500">
                                        Showing <span className="font-semibold">{Math.min((page - 1) * limit + 1, totalTickets)}</span> to{' '}
                                        <span className="font-semibold">{Math.min(page * limit, totalTickets)}</span> of{' '}
                                        <span className="font-semibold">{totalTickets}</span> tickets
                                    </p>
                                </div>
                                <div>
                                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                        <button
                                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                            disabled={page === 1}
                                            className="relative inline-flex items-center rounded-l-md px-2 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <span className="sr-only">Previous</span>
                                            <ChevronRight className="h-4 w-4 rotate-180" />
                                        </button>
                                        <span className="relative inline-flex items-center px-3 py-1.5 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 bg-white">
                                            Page {page} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={page === totalPages}
                                            className="relative inline-flex items-center rounded-r-md px-2 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <span className="sr-only">Next</span>
                                            <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Ticket Detail workspace - Right Column */}
                <div className={`lg:col-span-2 ${selectedTicketId ? 'block' : 'hidden lg:block lg:opacity-50'}`}>
                    
                    {detailsLoading ? (
                        <div className="text-center py-24 bg-white border border-gray-200 rounded-2xl shadow-sm">
                            <Clock className="h-10 w-10 text-gray-400 animate-spin mx-auto mb-3" />
                            <h3 className="font-bold text-gray-900">Loading details...</h3>
                        </div>
                    ) : selectedTicket ? (
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm flex flex-col h-[600px] w-full">
                            {/* Discussion Thread Header with consolidated metadata */}
                            <div className="bg-gradient-to-r from-slate-50 to-gray-50/60 p-4 border-b border-gray-100 rounded-t-xl space-y-2">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => setSelectedTicketId(null)}
                                            className="lg:hidden p-1 text-gray-500 hover:text-gray-700 -ml-1"
                                        >
                                            <ArrowLeft className="h-5 w-5" />
                                        </button>
                                        <h3 className="font-extrabold text-lg text-gray-950 leading-tight">
                                            {selectedTicket.title}
                                        </h3>
                                    </div>
                                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                                        String(selectedTicket.status || '').toLowerCase().replace(/[\s\-_]+/g, '') === 'rated'
                                            ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                            : getStatusStyle(selectedTicket.status)
                                    }`}>
                                        {String(selectedTicket.status || '').toLowerCase().replace(/[\s\-_]+/g, '') === 'rated'
                                            ? 'Closed'
                                            : selectedTicket.status}
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 font-medium">
                                    <span className="font-mono font-bold bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                        {selectedTicket.ticketId}
                                    </span>
                                    <span>•</span>
                                    <span>Category: <strong className="text-gray-700 font-semibold">{selectedTicket.ticketType?.name || 'General'}</strong></span>
                                    <span>•</span>
                                    <span>Priority: <strong className={`font-semibold ${selectedTicket.priority?.toLowerCase() === 'high' ? 'text-red-650' : selectedTicket.priority?.toLowerCase() === 'medium' ? 'text-blue-650' : 'text-gray-650'}`}>{selectedTicket.priority}</strong></span>
                                    {selectedTicket.concernedEmployee && (
                                        <>
                                            <span>•</span>
                                            <span>Assignee: <strong className="text-gray-700 font-semibold">{`${selectedTicket.concernedEmployee.firstName} ${selectedTicket.concernedEmployee.lastName || ''}`.trim()}</strong></span>
                                        </>
                                    )}
                                    {selectedTicket.department && (
                                        <>
                                            <span>•</span>
                                            <span>Dept: <strong className="text-gray-700 font-semibold">{selectedTicket.department}</strong></span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Sticky Action Banner below Header */}
                            {(() => {
                                const ns = String(selectedTicket.status || '')
                                    .toLowerCase()
                                    .replace(/[\s\-_]+/g, '');
                                const isOpen       = ns === 'open';
                                const isInProgress = ns === 'inprogress';
                                const isResolved   = ns === 'resolved';
                                const hasFeedback  = !!selectedTicket.feedback?.rating;

                                // Determine if the logged-in user is the ticket creator.
                                // IMPORTANT: backend login response sends:
                                //   `id`  = User model _id  (matches ticket.creator._id)
                                //   `_id` = Employee doc _id (DIFFERENT - do NOT use for creator check)
                                // ticket.creator is populated from the User model, so compare against `id`.
                                const creatorId = selectedTicket.creator?._id || selectedTicket.creator?.id;
                                // Use `id` first (User _id), fall back to `_id` only if `id` is absent
                                const currentUserId = currentUser?.id || currentUser?._id;
                                // Also allow email-based fallback in case IDs differ between environments
                                const creatorEmail = selectedTicket.creator?.email;
                                const currentUserEmail = currentUser?.email;
                                const isCreator = !!creatorId && !!currentUserId && (
                                    String(creatorId) === String(currentUserId) ||
                                    (!!creatorEmail && !!currentUserEmail && creatorEmail.toLowerCase() === currentUserEmail.toLowerCase())
                                );

                                const showBanner = (
                                     (isOpen && !isCreator && !isSuperAdmin) ||
                                     (isInProgress && !isCreator && !isSuperAdmin) ||
                                     ((isOpen || isInProgress) && isCreator) ||
                                     (isResolved && !hasFeedback && isCreator) ||
                                     (isResolved && !hasFeedback && !isCreator && !isSuperAdmin) ||
                                     hasFeedback
                                 );

                                 if (!showBanner) return null;

                                 return (
                                     <div className="bg-gray-50/70 p-4 border-b border-gray-100">
                                         {/* Status-change buttons — only visible to support staff, NOT to ticket creator */}
                                         {isOpen && !isCreator && !isSuperAdmin && (
                                             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                 <div className="text-xs text-gray-650 font-medium">
                                                     This ticket is currently <span className="font-semibold text-blue-700">Open</span>. Support staff can begin processing the request.
                                                 </div>
                                                 <Button
                                                     variant="outline"
                                                     onClick={() => updateTicketStatus('In Progress')}
                                                     className="w-full sm:w-auto text-center border-amber-300 hover:bg-amber-50 text-amber-700 hover:text-amber-800 py-1.5 px-4 text-xs font-semibold"
                                                 >
                                                     Start Processing
                                                 </Button>
                                             </div>
                                         )}

                                         {isInProgress && !isCreator && !isSuperAdmin && (
                                             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                 <div className="text-xs text-gray-650 font-medium">
                                                     This ticket is currently <span className="font-semibold text-amber-700">In Progress</span>. Mark it resolved when work is finished.
                                                 </div>
                                                 <Button
                                                     variant="primary"
                                                     onClick={() => updateTicketStatus('Resolved')}
                                                     className="w-full sm:w-auto text-center bg-green-600 hover:bg-green-750 text-white py-1.5 px-4 text-xs"
                                                 >
                                                     Mark as Resolved
                                                 </Button>
                                             </div>
                                         )}

                                         {/* Show a waiting message to the creator while ticket is being worked on */}
                                         {(isOpen || isInProgress) && isCreator && (
                                             <div className="text-xs text-gray-500 font-medium">
                                                 Your ticket is <span className="font-semibold text-amber-700">{selectedTicket.status}</span>. Our support team is working on it. You will be notified once resolved.
                                             </div>
                                         )}

                                         {/* Feedback form — only for ticket creator once resolved */}
                                         {isResolved && !hasFeedback && isCreator && (
                                             <div className="bg-green-50/50 p-4 rounded-xl border border-green-150 space-y-3">
                                                 <div className="flex items-center space-x-2 text-green-800 font-bold text-xs">
                                                     <CheckCircle className="h-4.5 w-4.5 text-green-600" />
                                                     <span>Ticket Resolved! Please leave your feedback:</span>
                                                 </div>
                                                 <div className="flex items-center space-x-1.5">
                                                     {[1, 2, 3, 4, 5].map(star => (
                                                         <Star
                                                             key={star}
                                                             onClick={() => setRating(star)}
                                                             className={`h-6 w-6 cursor-pointer transition-colors ${
                                                                 star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 hover:text-amber-300'
                                                             }`}
                                                         />
                                                     ))}
                                                 </div>
                                                 <div className="flex gap-2">
                                                     <textarea
                                                         placeholder="Add a remark (optional)"
                                                         value={feedbackComment}
                                                         onChange={(e) => setFeedbackComment(e.target.value)}
                                                         rows={2}
                                                         className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white"
                                                     />
                                                     <Button
                                                         variant="primary"
                                                         onClick={handleFeedbackSubmit}
                                                         disabled={rating === 0}
                                                         className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white text-xs px-4 self-end h-9"
                                                     >
                                                         Submit
                                                     </Button>
                                                 </div>
                                             </div>
                                         )}

                                         {/* Resolved info for support staff */}
                                         {isResolved && !hasFeedback && !isCreator && !isSuperAdmin && (
                                             <div className="flex items-center space-x-2 text-green-800 text-xs font-medium">
                                                 <CheckCircle className="h-4 w-4 text-green-600" />
                                                 <span>Ticket marked as resolved. Waiting for employee feedback.</span>
                                             </div>
                                         )}

                                        {hasFeedback && (
                                            <div className="bg-gray-50/80 p-3 rounded-xl border border-gray-200/80 flex items-start space-x-3 text-xs">
                                                <div className="flex-1">
                                                    <span className="font-semibold text-gray-500 block uppercase text-[10px] tracking-wider mb-1">Employee Feedback & Rating</span>
                                                    <div className="flex space-x-1 mb-1.5">
                                                        {[1, 2, 3, 4, 5].map(star => (
                                                            <Star
                                                                key={star}
                                                                className={`h-4 w-4 ${
                                                                    star <= selectedTicket.feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'
                                                                }`}
                                                            />
                                                        ))}
                                                    </div>
                                                    {selectedTicket.feedback.comment && (
                                                        <p className="text-gray-600 italic">
                                                            &ldquo;{selectedTicket.feedback.comment}&rdquo;
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Comments Timeline — Chat-style bubbles */}
                            <div 
                                ref={commentsContainerRef}
                                className="flex-1 overflow-y-auto p-4 space-y-4"
                            >

                                {/* Initial ticket description bubble */}
                                {(() => {
                                    const creatorId = selectedTicket.creator?._id || selectedTicket.creator?.id;
                                    const curId = currentUser?.id || currentUser?._id;
                                    const creatorEmail = selectedTicket.creator?.email;
                                    const curEmail = currentUser?.email;
                                    const isMine = !!creatorId && !!curId && (
                                        String(creatorId) === String(curId) ||
                                        (!!creatorEmail && !!curEmail && creatorEmail.toLowerCase() === curEmail.toLowerCase())
                                    );
                                    const initials = selectedTicket.creator
                                        ? `${selectedTicket.creator.firstName?.charAt(0) || ''}${selectedTicket.creator.lastName?.charAt(0) || ''}`.toUpperCase()
                                        : 'ME';
                                    const name = selectedTicket.creator
                                        ? `${selectedTicket.creator.firstName} ${selectedTicket.creator.lastName || ''}`.trim()
                                        : 'You';
                                    return (
                                        <div className={`flex items-end gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                                            <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-xs
                                                ${isMine ? 'bg-agrosaf-green/15 text-agrosaf-green' : 'bg-gray-200 text-gray-600'}`}>
                                                {initials}
                                            </div>
                                            <div className={`min-w-0 max-w-[72%] flex flex-col gap-1 ${isMine ? 'items-end' : 'items-start'}`}>
                                                <span className="text-[10px] text-gray-400 font-medium">
                                                    {isMine ? 'You' : name} · Raised Ticket
                                                </span>
                                                <div className={`w-full px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words overflow-hidden whitespace-pre-wrap
                                                    ${isMine
                                                        ? 'bg-agrosaf-green text-white rounded-br-none'
                                                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                                                    }`}>
                                                    {selectedTicket.description}
                                                </div>
                                                {selectedTicket.attachments?.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-1.5">
                                                        {selectedTicket.attachments.map((file, i) => (
                                                            isImageFile(file.fileName, file.fileUrl) ? (
                                                                <a key={i} href={file.fileUrl} target="_blank" rel="noopener noreferrer"
                                                                   className="block w-24 h-24 rounded-xl overflow-hidden border-2 border-white/30 hover:opacity-90 transition-opacity flex-shrink-0">
                                                                    <img src={file.fileUrl} alt={file.fileName}
                                                                         className="w-full h-full object-cover" />
                                                                </a>
                                                            ) : (
                                                                <button
                                                                    key={i}
                                                                    onClick={(e) => handleDownloadAttachment(e, file.fileUrl, file.fileName)}
                                                                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs border font-medium transition-opacity hover:opacity-80
                                                                        ${isMine ? 'bg-green-50/50 border-green-200/60 text-green-700 hover:bg-green-50' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                                                    <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                                                                    <span className="max-w-[120px] truncate">{file.fileName}</span>
                                                                    <Download className="h-3 w-3 flex-shrink-0" />
                                                                </button>
                                                            )
                                                        ))}
                                                    </div>
                                                )}
                                                <span className="text-[10px] text-gray-400">
                                                    {new Date(selectedTicket.createdAt).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* History / System event pills — always centred */}
                                {selectedTicket.history?.map((h, index) => (
                                    <div key={h.id || index} className="flex justify-center">
                                        <span className="text-[10px] text-gray-500 bg-gray-100 border border-gray-200/60 px-3 py-1 rounded-full">
                                            {h.author}: {h.text}
                                        </span>
                                    </div>
                                ))}

                                {/* Comment bubbles */}
                                {selectedTicket.comments?.map(c => {
                                    const commentAuthorId = c.author?._id || c.author?.id;
                                    const curId = currentUser?.id || currentUser?._id;
                                    const authorEmail = c.author?.email;
                                    const curEmail = currentUser?.email;
                                    const isMine = !!commentAuthorId && !!curId && (
                                        String(commentAuthorId) === String(curId) ||
                                         (!!authorEmail && !!curEmail && authorEmail.toLowerCase() === curEmail.toLowerCase())
                                    );
                                    const authorName = c.author
                                        ? `${c.author.firstName} ${c.author.lastName || ''}`.trim()
                                        : 'Unknown';
                                    const initials = c.author
                                        ? `${c.author.firstName?.charAt(0) || ''}${c.author.lastName?.charAt(0) || ''}`.toUpperCase()
                                        : 'ME';
                                    return (
                                        <div key={c._id} className={`group flex items-end gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                                            <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-xs
                                                ${isMine ? 'bg-agrosaf-green/15 text-agrosaf-green' : 'bg-gray-200 text-gray-600'}`}>
                                                {initials}
                                            </div>
                                            <div className={`min-w-0 max-w-[72%] flex flex-col gap-1 ${isMine ? 'items-end' : 'items-start'}`}>
                                                {!isMine && (
                                                    <span className="text-[10px] text-gray-400 font-medium">{authorName}</span>
                                                )}

                                                {/* Reply Quote block */}
                                                {c.replyTo && (
                                                    <div className={`w-full px-3 py-1.5 rounded-xl text-xs border-l-4 mb-0.5
                                                        ${isMine
                                                            ? 'bg-green-50 border-agrosaf-green text-green-800'
                                                            : 'bg-gray-100 border-gray-400 text-gray-600'}`}>
                                                        <span className="font-semibold block text-[10px] uppercase tracking-wide mb-0.5">
                                                            ↩ {c.replyTo.authorName}
                                                        </span>
                                                        <span className="line-clamp-2 italic">"{c.replyTo.content}"</span>
                                                    </div>
                                                )}

                                                <div className={`w-full px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words overflow-hidden
                                                    ${isMine
                                                        ? 'bg-agrosaf-green text-white rounded-br-none'
                                                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                                                    }`}>
                                                    {c.content}
                                                </div>

                                                {/* File Previews — images as squares, others as download cards */}
                                                {c.attachments?.length > 0 && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {c.attachments.map((file, idx) => (
                                                            isImageFile(file.fileName, file.fileUrl) ? (
                                                                <a key={idx} href={file.fileUrl} target="_blank" rel="noopener noreferrer"
                                                                   className="block w-24 h-24 rounded-xl overflow-hidden border-2 border-white/20 hover:opacity-90 transition-opacity flex-shrink-0">
                                                                    <img src={file.fileUrl} alt={file.fileName} className="w-full h-full object-cover" />
                                                                </a>
                                                            ) : (
                                                                <button
                                                                    key={idx}
                                                                    onClick={(e) => handleDownloadAttachment(e, file.fileUrl, file.fileName)}
                                                                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs border font-medium hover:opacity-80 transition-opacity
                                                                        ${isMine ? 'bg-green-50/50 border-green-200/60 text-green-700 hover:bg-green-50' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                                                    <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                                                                    <span className="max-w-[120px] truncate">{file.fileName}</span>
                                                                    <Download className="h-3 w-3 flex-shrink-0" />
                                                                </button>
                                                            )
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-3">
                                                    <span className="text-[10px] text-gray-400">
                                                        {new Date(c.timestamp).toLocaleString()}
                                                    </span>
                                                    {/* Reply button — visible on hover */}
                                                    {!isSuperAdmin && ['closed', 'rated'].indexOf(String(selectedTicket.status).toLowerCase()) === -1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setReplyingTo({ commentId: c._id, authorName, content: c.content })}
                                                            className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-agrosaf-green transition-all"
                                                        >
                                                            <CornerUpLeft className="h-3 w-3" /> Reply
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* End of comments scroll target container */}
                            </div>

                            {/* Comment Entry form footer */}
                            {!isSuperAdmin && ['closed', 'rated'].indexOf(String(selectedTicket.status).toLowerCase()) === -1 && (
                                <form onSubmit={handleAddComment} className="p-4 border-t border-gray-100 space-y-2 bg-gray-50/50">
                                    
                                    {/* Reply Banner */}
                                    {replyingTo && (
                                        <div className="flex items-center justify-between bg-agrosaf-green/5 border border-agrosaf-green/20 rounded-lg px-3 py-2 text-xs">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <CornerUpLeft className="h-3.5 w-3.5 text-agrosaf-green flex-shrink-0" />
                                                <span className="font-semibold text-agrosaf-green">{replyingTo.authorName}</span>
                                                <span className="truncate text-gray-500 italic">"{replyingTo.content.substring(0, 60)}{replyingTo.content.length > 60 ? '...' : ''}"</span>
                                            </div>
                                            <button type="button" onClick={() => setReplyingTo(null)}
                                                className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            placeholder={replyingTo ? `Reply to ${replyingTo.authorName}...` : 'Type a message or comment...'}
                                            value={commentText}
                                            onChange={(e) => setCommentText(e.target.value)}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white"
                                        />
                                        
                                        <label className={`p-2 border border-gray-300 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${uploadingCommentFiles ? 'text-agrosaf-green animate-pulse' : 'text-gray-500 hover:text-gray-700'}`}>
                                            {uploadingCommentFiles ? <Clock className="h-4.5 w-4.5 animate-spin" /> : <Paperclip className="h-4.5 w-4.5" />}
                                            <input
                                                type="file"
                                                multiple
                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf"
                                                className="hidden"
                                                disabled={uploadingCommentFiles}
                                                onChange={handleCommentFileAttach}
                                            />
                                        </label>

                                        <button
                                            type="submit"
                                            disabled={addCommentMutation.isPending}
                                            className="p-2 bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white rounded-lg transition-all disabled:opacity-50"
                                        >
                                            <Send className="h-4.5 w-4.5" />
                                        </button>
                                    </div>
                                    
                                    {/* Files list feedback */}
                                    {commentFiles.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 pt-1">
                                            {commentFiles.map((f, i) => (
                                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                                    ☁ {f.fileName || f}
                                                    <button
                                                        type="button"
                                                        onClick={() => setCommentFiles(commentFiles.filter((_, idx) => idx !== i))}
                                                        className="ml-1 text-gray-400 hover:text-gray-600"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </form>
                            )}
                        </div>
                    ) : (
                        <div className="hidden lg:flex h-[400px] border border-dashed border-gray-300 bg-white rounded-2xl items-center justify-center text-center p-8 shadow-sm">
                            <div>
                                <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-2.5" />
                                <h3 className="font-bold text-gray-900 mb-1">No Ticket Selected</h3>
                                <p className="text-sm text-gray-500 max-w-xs mx-auto">
                                    Select a ticket from the left panel to inspect its discussion thread, attachments, and progress.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Ticket Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-black/55 transition-opacity" onClick={() => setShowCreateModal(false)}></div>
                        
                        {/* Center modal */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                        
                        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full border border-gray-100 p-6 space-y-6">
                            
                            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                                <h3 className="text-lg font-extrabold text-gray-950">Raise New Ticket</h3>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleCreateTicket} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Subject / Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newTicketData.title}
                                        onChange={(e) => handleFieldChange('title', e.target.value)}
                                        onBlur={() => handleFieldBlur('title')}
                                        className={`w-full px-3.5 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-colors
                                            ${formErrors.title && formTouched.title
                                                ? 'border-red-400 focus:ring-red-300 bg-red-50'
                                                : 'border-gray-300 focus:ring-agrosaf-green'}`}
                                        placeholder="Brief summary of the issue..."
                                        maxLength={120}
                                    />
                                    {formErrors.title && formTouched.title && (
                                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                                            <span>⚠</span> {formErrors.title}
                                        </p>
                                    )}
                                    <p className="mt-0.5 text-[10px] text-gray-400 text-right">
                                        {newTicketData.title.length}/120
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                            Ticket Type <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={newTicketData.type}
                                            onChange={(e) => handleFieldChange('type', e.target.value)}
                                            onBlur={() => handleFieldBlur('type')}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm bg-white transition-colors
                                                ${formErrors.type && formTouched.type
                                                    ? 'border-red-400 focus:ring-red-300 bg-red-50'
                                                    : 'border-gray-300 focus:ring-agrosaf-green'}`}
                                        >
                                            <option value="">-- Select Type --</option>
                                            {ticketTypes.map(t => (
                                                <option key={t._id} value={t._id}>{t.name}</option>
                                            ))}
                                        </select>
                                        {formErrors.type && formTouched.type && (
                                            <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                                                <span>⚠</span> {formErrors.type}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                            Priority <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={newTicketData.priority}
                                            onChange={(e) => handleFieldChange('priority', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm bg-white"
                                        >
                                            <option value="Low">Low</option>
                                            <option value="Medium">Medium</option>
                                            <option value="High">High</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                            Target Department <span className="text-red-500">*</span>
                                        </label>
                                        <SearchableDropdown
                                            options={departmentsList.map(dept => {
                                                const name = typeof dept === 'string' ? dept : (dept.name || dept.title || '');
                                                return {
                                                    id: name,
                                                    displayText: name,
                                                    title: name
                                                };
                                            })}
                                            value={newTicketData.department || null}
                                            onChange={(dept) => {
                                                setNewTicketData(prev => ({ ...prev, department: dept || '', employeeId: '' }));
                                                setFormErrors(validateCreateForm({ ...newTicketData, department: dept || '', employeeId: '' }));
                                            }}
                                            onSearch={handleDeptSearch}
                                            onScrollToBottom={handleDeptScrollToBottom}
                                            hasMore={hasMoreDept}
                                            isLoadingMore={loadingMoreDept}
                                            loading={loadingDept}
                                            placeholder="-- Select Department --"
                                            searchPlaceholder="Search departments..."
                                            error={
                                                (formErrors.department && formTouched.department) || 
                                                (newTicketData.department && targetDeptEmployeeCount === 0) 
                                                    ? formErrors.department 
                                                    : null
                                            }
                                            onBlur={() => handleFieldBlur('department')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                            Target Employee <span className="text-red-500">*</span>
                                        </label>
                                        <SearchableDropdown
                                            options={employeesList
                                                .filter(emp => emp.userId && String(emp.userId) !== String(currentUser?.id || currentUser?._id))
                                                .map(emp => ({
                                                    id: emp.userId || emp.id,
                                                    displayText: `${emp.name} (${emp.employeeId || 'No ID'})`,
                                                    title: emp.name
                                                }))}
                                            value={newTicketData.employeeId || null}
                                            onChange={(empId) => handleFieldChange('employeeId', empId || '')}
                                            onSearch={handleEmpSearch}
                                            onScrollToBottom={handleEmpScrollToBottom}
                                            hasMore={hasMoreEmp}
                                            isLoadingMore={loadingMoreEmp}
                                            loading={loadingEmp}
                                            disabled={!newTicketData.department}
                                            placeholder={newTicketData.department ? "-- Select Target Employee --" : "Select Department First"}
                                            searchPlaceholder="Search employees..."
                                            error={formErrors.employeeId && formTouched.employeeId ? formErrors.employeeId : null}
                                            onBlur={() => handleFieldBlur('employeeId')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={newTicketData.description}
                                        onChange={(e) => handleFieldChange('description', e.target.value)}
                                        onBlur={() => handleFieldBlur('description')}
                                        rows={4}
                                        className={`w-full px-3.5 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm resize-none transition-colors
                                            ${formErrors.description && formTouched.description
                                                ? 'border-red-400 focus:ring-red-300 bg-red-50'
                                                : 'border-gray-300 focus:ring-agrosaf-green'}`}
                                        placeholder="Describe your issue or query in detail... (min. 10 characters)"
                                    />
                                    {formErrors.description && formTouched.description && (
                                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                                            <span>⚠</span> {formErrors.description}
                                        </p>
                                    )}
                                    <p className="mt-0.5 text-[10px] text-gray-400">
                                        {newTicketData.description.length} characters {newTicketData.description.length < 10 && newTicketData.description.length > 0 ? `(${10 - newTicketData.description.length} more needed)` : ''}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Attachments
                                    </label>
                                    <div 
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
                                            uploadingFiles ? 'border-agrosaf-green bg-green-50/30' : 
                                            isDragging ? 'border-agrosaf-green bg-green-50/20' : 'border-gray-300'
                                        }`}
                                    >
                                        <div className="space-y-1 text-center">
                                            {uploadingFiles ? (
                                                <>
                                                    <Clock className="mx-auto h-10 w-10 text-agrosaf-green animate-spin" />
                                                    <p className="text-sm text-agrosaf-green font-medium">Uploading to cloud...</p>
                                                </>
                                            ) : (
                                                <>
                                                    <Paperclip className="mx-auto h-10 w-10 text-gray-400" />
                                                    <div className="flex justify-center text-sm text-gray-600">
                                                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-agrosaf-green hover:text-agrosaf-darkGreen focus-within:outline-none">
                                                            <span>Upload files</span>
                                                            <input
                                                                type="file"
                                                                multiple
                                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf"
                                                                disabled={uploadingFiles}
                                                                onChange={handleFileAttach}
                                                                className="hidden"
                                                            />
                                                        </label>
                                                        <p className="pl-1">or drag and drop</p>
                                                    </div>
                                                    <p className="text-xs text-gray-500">
                                                        PNG, JPG, PDF, DOCX up to 25MB
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Uploaded Files Feedback */}
                                    {newTicketData.attachments.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-3">
                                            {newTicketData.attachments.map((f, i) => (
                                                <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                                    ☁ {f.fileName || f}
                                                    <button
                                                        type="button"
                                                        onClick={() => setNewTicketData(prev => ({
                                                            ...prev,
                                                            attachments: prev.attachments.filter((_, idx) => idx !== i)
                                                        }))}
                                                        className="ml-1 text-green-500 hover:text-green-700"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowCreateModal(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={createTicketMutation.isPending}
                                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white disabled:opacity-50"
                                    >
                                        Submit Ticket
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InternalTickets;
